import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

// Subcomponents
import RulesList from '../components/datasets/RulesList';
import RuleForm from '../components/datasets/RuleForm';
import DestinationsList from '../components/datasets/DestinationsList';
import DestinationForm from '../components/datasets/DestinationForm';
import BatchRunForm from '../components/datasets/BatchRunForm';

const DatasetDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useContext(AuthContext);

  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [showAddRule, setShowAddRule] = useState(false);
  const [showAddDestination, setShowAddDestination] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editingDestination, setEditingDestination] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [availableTransformers, setAvailableTransformers] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');

  // Fetch available transformers
  useEffect(() => {
    const fetchTransformers = async () => {
      try {
        const config = {
          headers: getAuthHeader(),
        };
        const { data } = await axios.get('/api/test-transformers/available', config);
        setAvailableTransformers(data.transformers);
      } catch (error) {
        console.error('Failed to fetch transformers:', error);
        // Default fallback if API fails
        setAvailableTransformers([
          { id: 'tallyDataTransformer', name: 'Tally data transformer' }
        ]);
      }
    };

    fetchTransformers();
  }, [getAuthHeader]);

  // Fetch dataset details
  useEffect(() => {
    const fetchDataset = async () => {
      try {
        setLoading(true);
        const config = {
          headers: getAuthHeader(),
        };

        const { data } = await axios.get(`/api/datasets/${id}`, config);
        setDataset(data);
        setError(null);
        // Construct webhook URL
        if (data && data._id) {
          const currentOrigin = window.location.origin;
          setWebhookUrl(`${currentOrigin}/api/webhook/${data._id}`);
        }
      } catch (error) {
        setError(
          error.response?.data?.message || 'Failed to load dataset. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDataset();
  }, [id, getAuthHeader]);

  // Handle copy webhook URL
  const handleCopyWebhookUrl = () => {
    if (!navigator.clipboard) {
      console.error('Clipboard API not available. Please use a secure context (HTTPS) or a compatible browser.');
      alert('Copying to clipboard is not available in this environment. Please ensure you are on HTTPS or a compatible browser.');
      return;
    }
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl).then(() => {
        setCopiedWebhook(true);
        setTimeout(() => setCopiedWebhook(false), 2000); // Reset after 2 seconds
      }).catch(err => {
        console.error('Failed to copy webhook URL: ', err);
      });
    }
  };

  // Handle name editing
  const startEditName = () => {
    setTempName(dataset.name);
    setEditingName(true);
  };

  const cancelEditName = () => {
    setEditingName(false);
    setTempName('');
  };

  const handleSaveName = async () => {
    if (!tempName.trim()) {
      setError('Dataset name cannot be empty');
      return;
    }

    try {
      const config = {
        headers: getAuthHeader(),
      };

      const { data } = await axios.put(
        `/api/datasets/${id}`,
        { ...dataset, name: tempName.trim() },
        config
      );
      
      setDataset(data);
      setEditingName(false);
      setTempName('');
      setError(null);
    } catch (error) {
      setError(
        error.response?.data?.message || 'Failed to update dataset name. Please try again.'
      );
    }
  };

  // Handle rule operations
  const handleAddRule = async (newRule) => {
    try {
      const config = {
        headers: getAuthHeader(),
      };

      const { data } = await axios.post(
        `/api/datasets/${id}/rules`,
        newRule,
        config
      );
      
      setDataset(data);
      setShowAddRule(false);
    } catch (error) {
      setError(
        error.response?.data?.message || 'Failed to add rule. Please try again.'
      );
    }
  };

  const handleEditRule = async (updatedRule) => {
    try {
      const config = {
        headers: getAuthHeader(),
      };

      const { data } = await axios.put(
        `/api/datasets/${id}/rules/${updatedRule._id}`,
        updatedRule,
        config
      );
      
      setDataset(data);
      setEditingRule(null);
    } catch (error) {
      setError(
        error.response?.data?.message || 'Failed to update rule. Please try again.'
      );
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      const config = {
        headers: getAuthHeader(),
      };

      const { data } = await axios.delete(
        `/api/datasets/${id}/rules/${ruleId}`,
        config
      );
      
      setDataset(data);
    } catch (error) {
      setError(
        error.response?.data?.message || 'Failed to delete rule. Please try again.'
      );
    }
  };

  // Start editing a rule
  const startEditRule = (rule) => {
    setEditingRule(rule);
    setShowAddRule(false);
  };

  // Cancel editing a rule
  const cancelEditRule = () => {
    setEditingRule(null);
  };

  // Handle destination operations
  const handleAddDestination = async (newDestination) => {
    try {
      const config = {
        headers: getAuthHeader(),
      };

      const { data } = await axios.post(
        `/api/datasets/${id}/destinations`,
        newDestination,
        config
      );
      
      setDataset(data);
      setShowAddDestination(false);
    } catch (error) {
      setError(
        error.response?.data?.message || 'Failed to add destination. Please try again.'
      );
    }
  };

  const handleEditDestination = async (updatedDestination) => {
    try {
      const config = {
        headers: getAuthHeader(),
      };

      const { data } = await axios.put(
        `/api/datasets/${id}/destinations/${updatedDestination._id}`,
        updatedDestination,
        config
      );
      
      setDataset(data);
      setEditingDestination(null);
    } catch (error) {
      setError(
        error.response?.data?.message || 'Failed to update destination. Please try again.'
      );
    }
  };

  const handleDeleteDestination = async (destinationId) => {
    try {
      const config = {
        headers: getAuthHeader(),
      };

      const { data } = await axios.delete(
        `/api/datasets/${id}/destinations/${destinationId}`,
        config
      );
      
      setDataset(data);
    } catch (error) {
      setError(
        error.response?.data?.message || 'Failed to delete destination. Please try again.'
      );
    }
  };

  // Start editing a destination
  const startEditDestination = (destination) => {
    setEditingDestination(destination);
    setShowAddDestination(false);
  };

  // Cancel editing a destination
  const cancelEditDestination = () => {
    setEditingDestination(null);
  };

  // Handle delete dataset
  const handleDeleteDataset = async () => {
    if (!window.confirm('Are you sure you want to delete this dataset?')) {
      return;
    }

    try {
      const config = {
        headers: getAuthHeader(),
      };

      await axios.delete(`/api/datasets/${id}`, config);
      navigate('/');
    } catch (error) {
      setError(
        error.response?.data?.message || 'Failed to delete dataset. Please try again.'
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center my-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="flex items-center mb-6">
          <button 
            onClick={() => navigate('/')} 
            className="mr-3 text-gray-500 hover:text-gray-700"
            aria-label="Go back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Error</h1>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
        
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!dataset) {
    return (
      <div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">Dataset not found</p>
            </div>
          </div>
        </div>
        
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/')} 
            className="mr-3 text-gray-500 hover:text-gray-700"
            aria-label="Go back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
          <div>
            <div className="flex items-center space-x-2">
              {editingName ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    className="text-2xl font-bold text-gray-900 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveName();
                      } else if (e.key === 'Escape') {
                        cancelEditName();
                      }
                    }}
                  />
                  <button
                    onClick={handleSaveName}
                    className="p-1 text-green-600 hover:text-green-700 focus:outline-none"
                    title="Save name"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={cancelEditName}
                    className="p-1 text-red-600 hover:text-red-700 focus:outline-none"
                    title="Cancel editing"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <h1 className="text-2xl font-bold text-gray-900">{dataset.name}</h1>
                  <button
                    onClick={startEditName}
                    className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                    title="Edit dataset name"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
              )}
              {webhookUrl && (
                <div className="flex items-center text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                  <span className="mr-1 truncate max-w-[200px] md:max-w-[300px]" title={webhookUrl}>{webhookUrl}</span>
                  <button
                    onClick={handleCopyWebhookUrl}
                    title="Copy Webhook URL"
                    className="p-1 text-gray-500 hover:text-primary-600 focus:outline-none rounded-md"
                  >
                    {copiedWebhook ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </div>
            {dataset.description && (
              <p className="text-gray-500 mt-1">{dataset.description}</p>
            )}
            {webhookUrl && copiedWebhook && <p className="text-xs text-green-600 mt-1">Webhook URL Copied!</p>}
          </div>
        </div>
        
        <div className="flex space-x-3">
          <Link
            to={`/test/${dataset._id}`}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Test Dataset
          </Link>
          
          <button
            onClick={handleDeleteDataset}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['details', 'rules', 'destinations', 'batch'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                // Reset editing state when switching tabs
                if (tab !== 'rules') {
                  setEditingRule(null);
                  setShowAddRule(false);
                }
                if (tab !== 'destinations') {
                  setEditingDestination(null);
                  setShowAddDestination(false);
                }
              }}
              className={`${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
            >
              {tab === 'batch' ? 'Batch Run' : tab}
              {tab === 'rules' && dataset.rules.length > 0 && (
                <span className="ml-2 bg-primary-100 text-primary-600 py-0.5 px-2 rounded-full text-xs">
                  {dataset.rules.length}
                </span>
              )}
              {tab === 'destinations' && dataset.destinations.length > 0 && (
                <span className="ml-2 bg-primary-100 text-primary-600 py-0.5 px-2 rounded-full text-xs">
                  {dataset.destinations.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mb-6">
        {activeTab === 'details' && (
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">              <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Data Transformer</h2>
              <div className="p-4 bg-gray-50 rounded-md">
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transform incoming data format
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    Choose a transformer to change how data is processed before applying rules
                  </p>
                  <select
                    value={dataset.transformer || 'none'}
                    onChange={async (e) => {
                      try {
                        const config = {
                          headers: getAuthHeader(),
                        };
                        const { data } = await axios.put(
                          `/api/datasets/${id}`,
                          { ...dataset, transformer: e.target.value },
                          config
                        );
                        setDataset(data);
                      } catch (error) {
                        setError(
                          error.response?.data?.message || 'Failed to update transformer setting'
                        );
                      }
                    }}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="none">None (Use original data)</option>
                    {availableTransformers.map(transformer => (
                      <option key={transformer.id} value={transformer.id}>
                        {transformer.name}
                      </option>
                    ))}
                  </select>
                </div>
                {dataset.transformer === 'tallyDataTransformer' && (
                  <div className="bg-blue-50 border border-blue-100 rounded-md p-3 mt-3">
                    <p className="text-sm text-blue-700">
                      <strong>Tally data transformer</strong> - Converts Tally.so form field arrays to a simplified JSON structure 
                      where field labels become keys and field values become values. Useful for form submissions.
                    </p>
                  </div>
                )}
              </div>
            </div>
                
            <h2 className="text-lg font-medium text-gray-900 mb-4">Dataset Schema</h2>
            {dataset.schema && Object.keys(dataset.schema).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(dataset.schema).map(([field, type]) => (
                  <div key={field} className="border rounded-md p-3">
                    <div className="font-medium">{field}</div>
                    <div className="text-sm text-gray-500">{type}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-md p-4 text-center">
                <p className="text-gray-500">
                  No schema defined for this dataset. You can add fields by creating enrichment rules.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'rules' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Enrichment Rules</h2>
              {!showAddRule && !editingRule && (
                <button
                  onClick={() => setShowAddRule(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Rule
                </button>
              )}
            </div>

            {showAddRule ? (
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-4">
                <h3 className="text-base font-medium text-gray-900 mb-4">Add New Rule</h3>
                <RuleForm
                  schema={dataset.schema}
                  rules={dataset.rules}
                  onSubmit={handleAddRule}
                  onCancel={() => setShowAddRule(false)}
                />
              </div>
            ) : null}

            {editingRule ? (
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-4">
                <h3 className="text-base font-medium text-gray-900 mb-4">Edit Rule</h3>
                <RuleForm
                  schema={dataset.schema}
                  rules={dataset.rules}
                  editingRule={editingRule}
                  onSubmit={handleEditRule}
                  onCancel={cancelEditRule}
                />
              </div>
            ) : null}

            {dataset.rules.length === 0 ? (
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-8 text-center">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-gray-900 font-medium text-lg mb-1">No rules defined</h3>
                <p className="text-gray-500 mb-6">Add your first enrichment rule to start processing data.</p>
                <button
                  onClick={() => setShowAddRule(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add First Rule
                </button>
              </div>
            ) : (
              <RulesList
                rules={dataset.rules}
                schema={dataset.schema}
                onEdit={startEditRule}
                onDelete={handleDeleteRule}
              />
            )}
          </div>
        )}

        {activeTab === 'destinations' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Data Destinations</h2>
              {!showAddDestination && !editingDestination && (
                <button
                  onClick={() => setShowAddDestination(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Destination
                </button>
              )}
            </div>

            {showAddDestination ? (
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-4">
                <h3 className="text-base font-medium text-gray-900 mb-4">Add New Destination</h3>
                <DestinationForm
                  onSubmit={handleAddDestination}
                  onCancel={() => setShowAddDestination(false)}
                />
              </div>
            ) : null}

            {editingDestination ? (
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-4">
                <h3 className="text-base font-medium text-gray-900 mb-4">Edit Destination</h3>
                <DestinationForm
                  editingDestination={editingDestination}
                  onSubmit={handleEditDestination}
                  onCancel={cancelEditDestination}
                />
              </div>
            ) : null}

            {dataset.destinations.length === 0 ? (
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-8 text-center">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                </div>
                <h3 className="text-gray-900 font-medium text-lg mb-1">No destinations configured</h3>
                <p className="text-gray-500 mb-6">Add a destination to receive your processed data.</p>
                <button
                  onClick={() => setShowAddDestination(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add First Destination
                </button>
              </div>
            ) : (
              <DestinationsList
                destinations={dataset.destinations}
                onEdit={startEditDestination}
                onDelete={handleDeleteDestination}
              />
            )}
          </div>
        )}

        {activeTab === 'batch' && (
          <div>
            <div className="mb-4">
              <h2 className="text-lg font-medium text-gray-900">Batch Enrichment</h2>
              <p className="text-sm text-gray-500 mt-1">
                Run a one-time batch enrichment on multiple records from a JSON file, CSV file, or Google Sheets.
              </p>
            </div>
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
              <BatchRunForm 
                datasetId={dataset._id}
                onComplete={(results) => {
                  console.log('Batch enrichment completed:', results);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatasetDetailsPage;